import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  createPersona,
  discussPersona,
  fetchSupportedLocalePairs,
  fetchUserProfile,
  localeToIso,
} from './api';
import { useSpendingCap } from './SpendingCapContext';
import SpendingCapNotice from './SpendingCapNotice';
import ChatInput from './ChatInput';
import FeedbackLink from './FeedbackLink';
import './Chat.css';
import './ChatList.css';
import './PersonaBuilder.css';

// Maps a persona-builder role to a chat-bubble sender for styling.
function roleToSender(role) {
  return role === 'USER' ? 'user' : 'persona';
}

// Renders text preserving its line breaks: the agent's replies may contain
// `\n`, which JSX would otherwise collapse into a single space.
function renderWithLineBreaks(text) {
  const lines = text.split('\n');
  return lines.map((line, index) => (
    <Fragment key={index}>
      {index > 0 && <br />}
      {line}
    </Fragment>
  ));
}

// Conversational persona builder: the user chats with an agent that interviews
// them and, once it has enough, proposes a persona draft they can create.
//
// The server is stateless, so `messages` holds the whole transcript and is
// replayed on every turn (see discussPersona / PersonaBuilderRequest). The
// conversation opens with an empty transcript: the server primes the agent's
// first question, so every entry in `messages` is a real, displayable turn.
function PersonaBuilder() {
  const { t } = useTranslation();
  // Every turn here is an LLM call, including the opening one Start fires, so the screen is
  // unusable while capped (#208). Unlike the chat there is no saved transcript to read back,
  // so there is no half-open state worth keeping: the builder simply cannot begin.
  const { capped } = useSpendingCap();
  // Read the latest translator inside the locale-loading effect without making it
  // a dependency: re-running that effect on a UI-language switch would refetch and
  // reset the user's chosen persona language.
  const tRef = useRef(t);
  tRef.current = t;
  const navigate = useNavigate();
  const goBack = () => navigate('/chats');

  // Setup phase: choosing the language the persona will speak.
  const [localePairs, setLocalePairs] = useState([]);
  const [originalLocaleIso, setOriginalLocaleIso] = useState('');
  const [started, setStarted] = useState(false);
  const [originalLocale, setOriginalLocale] = useState(null);
  // Whether the initial locale/profile load has finished, so an empty language
  // list can be shown as "you study no supported language" rather than a spinner.
  const [localesLoaded, setLocalesLoaded] = useState(false);

  // Conversation phase.
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [draft, setDraft] = useState(null);
  const [draftDismissed, setDraftDismissed] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');
  // Non-fatal: the readiness check errored, so no draft can appear this turn.
  // We keep chatting but warn the user rather than let them talk into the void.
  const [extractionWarning, setExtractionWarning] = useState(false);

  const messagesEndRef = useRef(null);

  // Load the persona locales the backend supports, restricted to the languages the
  // user is studying (a persona can only be built for a language you study), and
  // de-duplicated by ISO code.
  useEffect(() => {
    let cancelled = false;

    const loadLocales = async () => {
      try {
        const [pairs, profile] = await Promise.all([
          fetchSupportedLocalePairs(),
          fetchUserProfile(),
        ]);
        if (cancelled) return;

        const studiedCodes = new Set(
          (profile.studiedLanguages || []).map((s) => s.languageCode),
        );

        const seen = new Set();
        const locales = [];
        for (const pair of pairs) {
          if (!studiedCodes.has(pair.personaLocale.languageCode)) continue;
          const iso = localeToIso(pair.personaLocale);
          if (!seen.has(iso)) {
            seen.add(iso);
            locales.push(pair.personaLocale);
          }
        }

        setLocalePairs(locales);
        if (locales.length > 0) {
          setOriginalLocaleIso(localeToIso(locales[0]));
        }
      } catch (err) {
        if (!cancelled) setError(tRef.current('personaBuilder.loadLanguagesError'));
      } finally {
        if (!cancelled) setLocalesLoaded(true);
      }
    };

    loadLocales();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Sends one turn: appends `text` as a user turn (when given), replays the whole
  // transcript, then appends the agent's reply and stores any proposed draft.
  // `text` is null for the opening turn, where the server primes the greeting.
  const sendTurn = useCallback(
    async (text, locale) => {
      const nextMessages =
        text != null ? [...messages, { role: 'USER', text }] : messages;
      setMessages(nextMessages);
      setError('');
      setIsSending(true);

      try {
        const data = await discussPersona({
          messages: nextMessages,
          originalLocale: locale,
        });
        setMessages((prev) => [...prev, { role: 'ASSISTANT', text: data.reply }]);
        setExtractionWarning(Boolean(data.readinessCheckFailed));
        if (data.draft) {
          setDraft(data.draft);
          setDraftDismissed(false);
        }
      } catch (err) {
        setError(err.message || t('common.networkError'));
      } finally {
        setIsSending(false);
      }
    },
    [messages, t],
  );

  const handleStart = (e) => {
    e.preventDefault();
    if (capped) {
      return;
    }
    const locale = localePairs.find((l) => localeToIso(l) === originalLocaleIso);
    if (!locale) {
      setError(t('personaBuilder.chooseLanguage'));
      return;
    }
    setOriginalLocale(locale);
    setStarted(true);
    sendTurn(null, locale);
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    const text = inputValue.trim();
    if (!text || isSending || capped) return;
    setInputValue('');
    sendTurn(text, originalLocale);
  };

  const handleCreate = async () => {
    if (!draft || isCreating) return;
    setError('');
    setIsCreating(true);
    try {
      await createPersona({
        name: draft.name,
        description: draft.description,
        script: draft.script,
        voice: draft.voice ?? null,
        originalLocale,
      });
      navigate('/chats');
    } catch (err) {
      setError(err.message || t('personaBuilder.createError'));
    } finally {
      setIsCreating(false);
    }
  };

  const showDraft = draft && !draftDismissed;

  return (
    <div className="chat-list-container form-page-container">
      <div className="chat-list-header form-page-header">
        <h2>{t('personaBuilder.title')}</h2>
      </div>

      <div className="chat-list-content">
        {!started && localesLoaded && localePairs.length === 0 ? (
          <div className="create-chat-form">
            <p className="persona-builder-intro">
              {t('personaBuilder.noStudiedLanguages')}
            </p>
            <div className="create-chat-actions">
              <button type="button" className="cancel-button" onClick={goBack}>
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="create-button"
                onClick={() => navigate('/profile')}
              >
                {t('personaBuilder.goToProfile')}
              </button>
            </div>
          </div>
        ) : !started ? (
          <form className="create-chat-form" onSubmit={handleStart}>
            <p className="persona-builder-intro">
              {t('personaBuilder.intro')}
            </p>
            <div className="form-group">
              <label htmlFor="persona-builder-locale">{t('personaBuilder.personaLanguage')}</label>
              <select
                id="persona-builder-locale"
                value={originalLocaleIso}
                onChange={(e) => setOriginalLocaleIso(e.target.value)}
                required
                disabled={localePairs.length === 0}
              >
                {localePairs.length === 0 && (
                  <option value="">{t('personaBuilder.noLanguages')}</option>
                )}
                {localePairs.map((locale) => {
                  const iso = localeToIso(locale);
                  return (
                    <option key={iso} value={iso}>
                      {t(`locales.${iso}`)}
                    </option>
                  );
                })}
              </select>
            </div>
            {error && <div className="error-message">{error}</div>}
            {capped && <SpendingCapNotice />}
            <div className="create-chat-actions">
              <button type="button" className="cancel-button" onClick={goBack}>
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                className="create-button"
                disabled={!originalLocaleIso || capped}
              >
                {t('personaBuilder.start')}
              </button>
            </div>
          </form>
        ) : (
          <>
            <div className="chat-messages persona-builder-messages">
              {messages.map((message, index) => (
                <div key={index} className={`message ${roleToSender(message.role)}`}>
                  <div className="message-content">
                    {renderWithLineBreaks(message.text)}
                  </div>
                </div>
              ))}
              {isSending && <div className="chat-list-status">{t('personaBuilder.thinking')}</div>}
              <div ref={messagesEndRef} />
            </div>

            {showDraft && (
              <div className="persona-draft-card">
                <div className="persona-draft-title">{t('personaBuilder.proposedPersona')}</div>
                <div className="persona-draft-field">
                  <span className="persona-draft-label">{t('personaBuilder.name')}</span>
                  <span>{draft.name}</span>
                </div>
                <div className="persona-draft-field">
                  <span className="persona-draft-label">{t('personaBuilder.description')}</span>
                  <span>{draft.description}</span>
                </div>
                <pre className="persona-draft-script">{draft.script}</pre>
                <div className="persona-draft-actions">
                  <button
                    type="button"
                    className="cancel-button"
                    onClick={() => setDraftDismissed(true)}
                    disabled={isCreating}
                  >
                    {t('personaBuilder.keepRefining')}
                  </button>
                  <button
                    type="button"
                    className="create-button"
                    onClick={handleCreate}
                    disabled={isCreating}
                  >
                    {isCreating ? t('personaBuilder.creating') : t('personaBuilder.create')}
                  </button>
                </div>
              </div>
            )}

            {extractionWarning && (
              <div className="persona-builder-warning">
                {t('personaBuilder.notEnoughInfo')}
              </div>
            )}

            {error && <div className="error-message">{error}</div>}

            {capped && <SpendingCapNotice />}
            <form className="chat-input-form" onSubmit={handleSendMessage}>
              <ChatInput
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onSubmit={handleSendMessage}
                placeholder={t('personaBuilder.describePlaceholder')}
                disabled={isSending || capped}
              />
              <button type="submit" className="chat-send-button" disabled={isSending || capped}>
                {isSending ? t('common.sending') : t('common.send')}
              </button>
            </form>
            <FeedbackLink />
          </>
        )}
      </div>
    </div>
  );
}

export default PersonaBuilder;
